import {AUCTION_TYPES} from '../misc/constants';
import {StateEnforcedAuctionOffer} from './enforced-auction-offer/enforced-auction-offer.reducers';
import {StateOffer} from './offer/offer.reducers';
import {StateSalesOffer} from './sales-offer/sales-offer.reducers';

/**
 * Update enforced auction offer state with new bid general status information.
 *
 * Only the current distribution channel is updated, as other channels will be newly fetched from backend on change.
 *
 * @param state the enforced auction offer state to update
 * @param bidGeneralStatus the bid general status to update the enforced auction offer state
 *
 * @return the update enforced auction offer state
 */
export function updateStateEnforcedAuctionOfferWithBidGeneralStatus(state: StateEnforcedAuctionOffer, bidGeneralStatus: BidGeneralStatusDto): StateEnforcedAuctionOffer {
  updateOfferInOffersGeneral(state.offers, bidGeneralStatus);
  return state;
}

/**
 * Update enforced auction offer state with new bid dealer specific status information.
 *
 * Only the current distribution channel is updated, as other channels will be newly fetched from backend on change.
 *
 * @param state the enforced auction offer state to update
 * @param bidDealerStatus the bid dealer specific status to update the enforced auction offer state
 *
 * @return the update enforced auction offer state
 */
export function updateStateEnforcedAuctionOfferWithBidDealerStatus(state: StateEnforcedAuctionOffer, bidDealerStatus: BidDealerStatusDto): StateEnforcedAuctionOffer {
  updateOfferInOffersDealerSpecific(state.offers, bidDealerStatus);
  return state;
}

/**
 * Update sales offer state with new bid general status information.
 *
 * Only the current distribution channel is updated, as other channels will be newly fetched from backend on change.
 *
 * @param state the sales offer state to update
 * @param bidGeneralStatus the bid general status to update the sales offer state
 *
 * @return the update sales offer state
 */
export function updateStateSalesOfferWithBidGeneralStatus(state: StateSalesOffer, bidGeneralStatus: BidGeneralStatusDto): StateSalesOffer {
  updateOfferInOffersGeneral(state.offers, bidGeneralStatus);
  return state;
}

/**
 * Update sales offer state with new bid dealer specific status information.
 *
 * Only the current distribution channel is updated, as other channels will be newly fetched from backend on change.
 *
 * @param state the sales offer state to update
 * @param bidDealerStatus the bid dealer specific status to update the sales offer state
 *
 * @return the update sales offer state
 */
export function updateStateSalesOfferWithBidDealerStatus(state: StateSalesOffer, bidDealerStatus: BidDealerStatusDto): StateSalesOffer {
  updateOfferInOffersDealerSpecific(state.offers, bidDealerStatus);
  return state;
}

/**
 * Update offer state with new bid general status information.
 *
 * Only the current distribution channel is updated, as other channels will be newly fetched from backend on change.
 *
 * @param state the offer state to update
 * @param bidGeneralStatus the bid general status to update the offer state
 *
 * @return the update offer state
 */
export function updateStateOfferWithBidGeneralStatus(state: StateOffer, bidGeneralStatus: BidGeneralStatusDto): StateOffer {

  // as other channels will be newly fetched anyway, there is no need to update bid status for them
  // only update the currently selected channel
  switch (state.pageSettings.channel) {
  case 'PIA':
    updateOfferInOffersGeneral(state.bookmarkedOffersPIA, bidGeneralStatus);
    updateOfferInOffersGeneral(state.finishedUserCompanyOffersPIA, bidGeneralStatus);
    updateOfferInOffersGeneral(state.currentUserCompanyOffersPIA, bidGeneralStatus);
    break;
  case 'PB':
    updateOfferInOffersGeneral(state.bookmarkedOffersPB, bidGeneralStatus);
    updateOfferInOffersGeneral(state.finishedUserCompanyOffersPB, bidGeneralStatus);
    updateOfferInOffersGeneral(state.currentUserCompanyOffersPB, bidGeneralStatus);
    break;
  case 'ALL_UC':
    updateOfferInOffersGeneral(state.bookmarkedOffersALLUC, bidGeneralStatus);
    updateOfferInOffersGeneral(state.finishedUserCompanyOffersALLUC, bidGeneralStatus);
    updateOfferInOffersGeneral(state.currentUserCompanyOffersALLUC, bidGeneralStatus);
    break;
  case 'DIN_BIL':
    updateOfferInOffersGeneral(state.bookmarkedOffersDINBIL, bidGeneralStatus);
    updateOfferInOffersGeneral(state.finishedUserCompanyOffersDINBIL, bidGeneralStatus);
    updateOfferInOffersGeneral(state.currentUserCompanyOffersDINBIL, bidGeneralStatus);
    break;
  case 'VGRX':
    updateOfferInOffersGeneral(state.bookmarkedOffersVGRX, bidGeneralStatus);
    updateOfferInOffersGeneral(state.finishedUserCompanyOffersVGRX, bidGeneralStatus);
    updateOfferInOffersGeneral(state.currentUserCompanyOffersVGRX, bidGeneralStatus);
    break;
  default:
      // no channel set, disregard update
  }

  // try updating a detail view's offer
  if (state.offer && state.offer.id === bidGeneralStatus.offerId && AUCTION_TYPES.includes(state.offer.offerType.data)) {
    setBidGeneralStatusState(bidGeneralStatus, state);

    // fail-safe: we clear the dealer-specific offer state properties when we receive a general update
    // this way we mitigate potential inconsistency in case the following bid dealer status message gets lost
    // (i.e. better no specific information than outdated information)
    // in a normal case, this will be immediately set again with the subsequent dealer-specific message
    clearBidDealerStatusState(state);
  }

  return state;
}

/**
 * Update offer state with new bid dealer specific status information.
 *
 * Only the current distribution channel is updated, as other channels will be newly fetched from backend on change.
 *
 * @param state the offer state to update
 * @param bidDealerStatus the bid dealer specific status to update the offer state
 *
 * @return the update offer state
 */
export function updateStateOfferWithBidDealerStatus(state: StateOffer, bidDealerStatus: BidDealerStatusDto): StateOffer {

  // as other channels will be newly fetched anyway, there is no need to update bid status for them
  // only update the currently selected channel
  switch (state.pageSettings.channel) {
  case 'PIA':
    updateOfferInOffersDealerSpecific(state.bookmarkedOffersPIA, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.finishedUserCompanyOffersPIA, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.currentUserCompanyOffersPIA, bidDealerStatus);
    break;
  case 'PB':
    updateOfferInOffersDealerSpecific(state.bookmarkedOffersPB, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.finishedUserCompanyOffersPB, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.currentUserCompanyOffersPB, bidDealerStatus);
    break;
  case 'ALL_UC':
    updateOfferInOffersDealerSpecific(state.bookmarkedOffersALLUC, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.finishedUserCompanyOffersALLUC, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.currentUserCompanyOffersALLUC, bidDealerStatus);
    break;
  case 'DIN_BIL':
    updateOfferInOffersDealerSpecific(state.bookmarkedOffersDINBIL, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.finishedUserCompanyOffersDINBIL, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.currentUserCompanyOffersDINBIL, bidDealerStatus);
    break;
  case 'VGRX':
    updateOfferInOffersDealerSpecific(state.bookmarkedOffersVGRX, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.finishedUserCompanyOffersVGRX, bidDealerStatus);
    updateOfferInOffersDealerSpecific(state.currentUserCompanyOffersVGRX, bidDealerStatus);
    break;
  default:
      // no channel set, disregard update
  }

  // try updating a detail view's offer
  if (state.offer && state.offer.id === bidDealerStatus?.generalStatusDto?.offerId && AUCTION_TYPES.includes(state.offer.offerType.data)) {
    // as fail-safe - in case of lost bidGeneralStatus websocket message - we set the general status (again)
    setBidGeneralStatusState(bidDealerStatus.generalStatusDto, state);

    setBidDealerStatusState(bidDealerStatus, state);
  }

  return state;
}

/**
 * Updates an offer of our (sub-)state with the updated information from bid general status dto.
 *
 * @param offers    the offer dto array containing the auction offer to update
 * @param bidGeneralStatus the new bid general status to update the auction offer
 */
function updateOfferInOffersGeneral(offers: OfferDto[], bidGeneralStatus: BidGeneralStatusDto) {
  if (offers) {
    // find the offer we want to update
    const offerToUpdate = offers['content']
      .find(offer => offer.id === bidGeneralStatus.offerId && AUCTION_TYPES.includes(offer.offerType.data));

    updateOfferBidDataGeneral(offerToUpdate, bidGeneralStatus);
  }
}

export function updateOfferBidDataGeneral(offerToUpdate: OfferDto, bidGeneralStatus: BidGeneralStatusDto) {
  if (offerToUpdate) {
    setBidGeneralStatusOfferToUpdate(bidGeneralStatus, offerToUpdate);

    // fail-safe: we clear the dealer-specific offer state properties when we receive a general update
    // this way we mitigate potential inconsistency in case the following bid dealer status message gets lost
    // (i.e. better no specific information than outdated information)
    // in a normal case, this will be immediately set again with the subsequent dealer-specific message
    clearBidDealerStatusOfferToUpdate(offerToUpdate);
  }
}

/**
 * Updates an offer of our (sub-)state with the updated information from bid dealer specific status dto.
 *
 * @param offers    the offer dto array containing the auction offer to update
 * @param bidDealerStatus the new bid dealer specific status to update the auction offer
 */
export function updateOfferInOffersDealerSpecific(offers: OfferDto[], bidDealerStatus: BidDealerStatusDto) {
  if (offers) {
    // find the offer we want to update
    const offerToUpdate = offers['content']
      .find(offer => offer.id === bidDealerStatus.generalStatusDto?.offerId && AUCTION_TYPES.includes(offer.offerType.data));

    updateOfferBidDataDealerSpecific(offerToUpdate, bidDealerStatus);
  }
}

export function updateOfferBidDataDealerSpecific(offerToUpdate: OfferDto, bidDealerStatus: BidDealerStatusDto) {
  if (offerToUpdate) {
    // as fail-safe - in case of lost bidGeneralStatus websocket message - we set the general status (again)
    setBidGeneralStatusOfferToUpdate(bidDealerStatus.generalStatusDto, offerToUpdate);

    setBidDealerStatusOfferToUpdate(bidDealerStatus, offerToUpdate);
  }
}

export function setBidGeneralStatusOfferToUpdate(bidGeneralStatus: BidGeneralStatusDto, offerToUpdate) {
  if (bidGeneralStatus.expiration) {
    offerToUpdate.expiration = bidGeneralStatus.expiration;
  }
  if (bidGeneralStatus.highestBid) {
    offerToUpdate.highestBid = bidGeneralStatus.highestBid;
  }
  if (bidGeneralStatus.minimumBid) {
    offerToUpdate.minimumBid = bidGeneralStatus.minimumBid;
  }
}

export function setBidDealerStatusOfferToUpdate(bidDealerStatus: BidDealerStatusDto, offerToUpdate) {
  if (bidDealerStatus.bidStatus) {
    offerToUpdate.bidStatus = bidDealerStatus.bidStatus;
  }
  if (bidDealerStatus.maximumBid) {
    offerToUpdate.maximumBid = bidDealerStatus.maximumBid;
  }
  if (bidDealerStatus.lastHighestBid) {
    offerToUpdate.myLastHighestBid = bidDealerStatus.lastHighestBid;
  }
  if (bidDealerStatus.lastMaximumBid) {
    offerToUpdate.myLastMaximumBid = bidDealerStatus.lastMaximumBid;
  }
}

export function clearBidDealerStatusOfferToUpdate(offerToUpdate) {
  offerToUpdate.bidStatus = undefined;
  offerToUpdate.maximumBid = undefined;
  offerToUpdate.myLastHighestBid = undefined;
  offerToUpdate.myLastMaximumBid = undefined;
}

function setBidGeneralStatusState(bidGeneralStatus: BidGeneralStatusDto, state: StateOffer) {
  if (bidGeneralStatus.expiration) {
    (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).expiration = bidGeneralStatus.expiration;
  }
  if (bidGeneralStatus.highestBid) {
    (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).highestBid = bidGeneralStatus.highestBid;
  }
  if (bidGeneralStatus.minimumBid) {
    (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).minimumBid = bidGeneralStatus.minimumBid;
  }
}

function clearBidDealerStatusState(state: StateOffer) {
  (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).bidStatus = undefined;
  (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).maximumBid = undefined;
  (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).myLastHighestBid = undefined;
  (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).myLastMaximumBid = undefined;
}

function setBidDealerStatusState(bidDealerStatus: BidDealerStatusDto, state: StateOffer) {
  if (bidDealerStatus.bidStatus) {
    (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).bidStatus = bidDealerStatus.bidStatus;
  }
  if (bidDealerStatus.maximumBid) {
    (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).maximumBid = bidDealerStatus.maximumBid;
  }
  if (bidDealerStatus.lastHighestBid) {
    (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).myLastHighestBid = bidDealerStatus.lastHighestBid;
  }
  if (bidDealerStatus.lastMaximumBid) {
    (state.offer as AuctionOfferDto | EnforcedAuctionOfferDto).myLastMaximumBid = bidDealerStatus.lastMaximumBid;
  }
}
